import { Modal, ModalProps } from 'antd';
import { useEffect, useState } from 'react';
import { DepartmentsSelect } from '../ImplementData/components/Filter/DepartmentSelect';
import { usePlatformSelect, useStoreTypeSelect } from '../ImplementData/components/Filter/Provider';
import { FilterStoreInfo } from '../ImplementData/components/Filter/useFilter';
import StoreSelector from '../ImplementData/components/Filter/StoreSelector';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { useTranslate } from '@tolgee/react';
import { exportPlatformData } from './service';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'components/DatePicker';
import { css } from '@emotion/css';

function ExportModal({
  platforms,
  departments,
  visible = false,
  ...otherProps
}: ModalProps & {
  platforms: General.PlatformUpperCase[];
  departments: Dashboard.Department[];
}) {
  const { t } = useTranslate();

  const [_departments, setDepartments] = useState<Dashboard.Department[]>([]);
  const [platform, setPlatform] = useState<General.PlatformUpperCase | 'ALL'>('ALL');
  const [storeType, setStoreType] = useState<Dashboard.ReceptionScore.StoreType>();
  const [storeInfo, setStoreInfo] = useState<FilterStoreInfo>({});
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(8, 'days').startOf('days'),
    dayjs().subtract(1, 'days').endOf('days'),
  ]);

  const departmentsSelect = (
    <DepartmentsSelect
      dashboardType="store_performance"
      value={_departments}
      onChange={setDepartments}
    />
  );

  useEffect(() => {
    if (departments.length && !_departments.length) {
      setDepartments([departments[0]]);
    }
  }, [departments, _departments]);

  const { regionInfo, platformSelect } = usePlatformSelect({
    scene: 'EXPORT_PLATFORM_DATA',
    departments: _departments,
    showPlatformAll: true,
    value: platform,
    platforms,
    onChange: (platform) => {
      setPlatform(platform);
      setStoreInfo({});
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: storeType,
    onChange: (storeType) => {
      setStoreType(storeType);
      setStoreInfo({});
    },
  });

  const storeInfoSelect = (
    <StoreSelector
      key={`${platform}-${storeType}`}
      filter={{
        storeInfo,
        storeType,
        platform,
        departments: _departments.length ? _departments : departments,
      }}
      onChange={setStoreInfo}
      regions={regionInfo.regionList as any}
      scene="EXPORT_PLATFORM_DATA"
    />
  );

  const [dates, setDates] = useState<[Dayjs | undefined, Dayjs | undefined] | undefined>();

  const rangeSelect = (
    <DatePicker.RangePicker
      value={range}
      disabledDate={(date) => {
        if (date.isSameOrAfter(dayjs(), 'days')) {
          return true;
        }
        if (dates) {
          const [start, end] = dates;
          const tooLate = start && date.diff(start, 'days') > 30;
          const tooEarly = end && end.diff(date, 'days') > 30;
          return Boolean(tooEarly) || Boolean(tooLate);
        }
        return false;
      }}
      onCalendarChange={(value) => {
        const [start, end] = value || [];
        setDates([start, end] as any);
      }}
      onChange={(value) => {
        const [start, end] = value || [];
        if (start) {
          setRange([start.startOf('days'), (end ?? start).endOf('days')]);
        }
      }}
      onOpenChange={(open) => {
        if (open) {
          setDates(undefined);
        }
      }}
    />
  );

  const { run: onDownload } = useExportFile();

  return (
    <Modal
      className={modalClassName}
      width={680}
      visible={visible}
      title={
        <div>
          {t('export_data')}
          <span>{t('导出数据提示')}</span>
        </div>
      }
      okText={t('导出')}
      onOk={(e) => {
        onDownload(() =>
          exportPlatformData({
            platform: platform === 'ALL' ? undefined : platform,
            departmentIds: _departments.map(({ id }) => id),
            storeType,
            storeIds: storeInfo.list?.map(({ id }) => id) || [],
            startTime: range[0].format('YYYY-MM-DD HH:mm:ss'),
            endTime: range[1].format('YYYY-MM-DD HH:mm:ss'),
          }),
        );
        otherProps.onCancel?.(e);
      }}
      {...otherProps}
    >
      <section>
        {departmentsSelect}
        {platformSelect}
        {storeTypeSelect}
        {storeInfoSelect}
      </section>
      {rangeSelect}
    </Modal>
  );
}

const modalClassName = css`
  .ant-modal-title > div {
    display: flex;
    align-items: center;
    > span {
      margin-left: 14px;
      font-size: 14px;
      font-weight: 400;
      color: #84868c;
    }
  }
  .ant-modal-body > section {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    > * {
      flex: 1 0 0;
      margin-right: 12px;
    }
  }
`;

export default ExportModal;
